import { PW_SERVER, withBearer } from "./config";
import { request, ApiRequestError } from "./client";
import { csrfHeaders, withAuthCookieLock } from "./refresh";
import type { LoginCredentials } from "../types";

const server = PW_SERVER;
let loginChallenge: {
  email: string;
  accountType: string;
  proof: string;
  expires: number;
} | null = null;
function challengeFor(
  email: string,
  accountType = localStorage.getItem("accountType") || "CORPORATE",
): string | undefined {
  return loginChallenge &&
    loginChallenge.expires > Date.now() &&
    loginChallenge.email === email.trim().toLowerCase() &&
    loginChallenge.accountType.toUpperCase() === accountType.toUpperCase()
    ? loginChallenge.proof
    : undefined;
}

const storedUser = (): Record<string, unknown> => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
  } catch {
    return {};
  }
};

// Explicit Authorization header for callers that pass a specific token.
const authFor = (token?: string) =>
  token ? { headers: { Authorization: withBearer(token) } } : {};

// ---------------------------------------------------------------------------
// Auth API — mirrors the old pw-cms-web authService.ts / authActions.ts
// All endpoints hit the PlanetWise access-manager at VITE_PW_SERVER.
// ---------------------------------------------------------------------------

// ---- Login ----

export interface LoginResponse {
  token?: string;
  redirectToOTP?: boolean;
  emailAuthRequired?: boolean;
  appAuthRequired?: boolean;
  redirectToPending?: boolean;
  userProfile?: Record<string, unknown>;
  role?: number;
  company?: string;
  bunits?: string;
  [key: string]: unknown;
}

export const login = async (
  credentials: LoginCredentials & { accountType?: string; companyId?: string },
): Promise<LoginResponse> => {
  const requestBody: Record<string, string> = {
    email: credentials.email,
    password: credentials.password,
    accountType: credentials.accountType || "CORPORATE",
  };
  if (credentials.companyId) requestBody.companyId = credentials.companyId;

  try {
    loginChallenge = null;
    const response = await request<LoginResponse>(
      "post",
      `${server}/accessmanager/user/v1/signin`,
      {
        data: requestBody,
        skipAuth: true,
        unwrap: false,
      },
    );
    if (typeof response.authChallenge === "string") {
      loginChallenge = {
        email: credentials.email.trim().toLowerCase(),
        accountType: requestBody.accountType,
        proof: response.authChallenge,
        expires: Date.now() + 600_000,
      };
    }
    return response;
  } catch (error) {
    if (error instanceof ApiRequestError) {
      const body = error.response?.data as
        { error?: unknown; message?: string } | undefined;
      const errField = body?.error;
      const message =
        (typeof errField === "object" && errField
          ? (errField as { message?: string }).message
          : undefined) ||
        body?.message ||
        (typeof errField === "string" ? errField : undefined) ||
        error.message;

      if (error.status === 401 || error.status === 403) {
        throw new Error(
          message ||
            "Invalid credentials. Please check your email and password.",
          { cause: error },
        );
      }
      if (error.status === 0) {
        throw new Error(
          "Unable to connect to the server. Please check your internet connection.",
          { cause: error },
        );
      }
    }
    const message = error instanceof Error ? error.message : "";
    throw new Error(message || "Login failed. Please try again.", {
      cause: error,
    });
  }
};

// ---- MFA / 2FA ----

export const verify2FAAuthOTP = (payload: {
  username: string;
  otp: string;
  type: "emailAuth" | "appAuth";
  generateToken?: boolean;
  accountType?: string;
}): Promise<LoginResponse> =>
  request("post", `${server}/accessmanager/user/v1/verify-2fa-auth-otp`, {
    data: {
      ...payload,
      authChallenge: challengeFor(payload.username, payload.accountType),
      accountType:
        payload.accountType ||
        localStorage.getItem("accountType") ||
        "CORPORATE",
    },
    skipAuth: true,
    unwrap: false,
  });

export const sendEmailAuthOTP = (
  email: string,
  accountType?: string,
): Promise<{ message: string }> =>
  request("post", `${server}/accessmanager/user/v1/email-auth-otp`, {
    data: {
      email,
      authChallenge: challengeFor(email, accountType),
      accountType:
        accountType || localStorage.getItem("accountType") || "CORPORATE",
    },
    skipAuth: true,
    unwrap: false,
  });

export const getAppAuthSecret = (
  userName: string,
  currentPassword: string,
  existingOtp?: string,
): Promise<{ qrCode: string; setupToken: string }> =>
  request("post", `${server}/user/v1/generate-app-auth-qr`, {
    data: { userName, currentPassword, existingOtp },
    unwrap: false,
  });

export const verifyAndSetAppAuth = (payload: {
  userName: string;
  otp: string;
  setupToken: string;
}): Promise<{ message: string; setupCompleted?: boolean }> =>
  request("post", `${server}/user/v1/verify-and-set-app-auth`, {
    data: payload,
    unwrap: false,
  });

// ---- Registration ----

export interface SignUpAddress {
  addressLine?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  cityCode?: string;
  state?: string;
  stateCode?: string;
  country?: string;
  countryCode?: string;
  pinCode?: string;
  timeZone?: string;
}

interface SignUpResponse {
  message: string;
  user?: {
    id?: string | number;
    username?: string;
  };
  company?: string | number;
  companyName?: string;
  [key: string]: unknown;
}

interface ZohoCustomerPayload {
  email: string;
  companyName: string;
  companyId: string | number;
  planId?: string;
  planCode?: string;
  user: string | number;
}

export const signUp = (payload: {
  company: string;
  industry?: string;
  industryId?: number;
  registrationId: string;
  username: string;
  email: string;
  password: string;
  accountType: string;
  type?: string;
  address?: SignUpAddress;
  planId?: string;
  planCode?: string;
  supplierId?: string;
  otp?: string;
  userDisplayName?: string;
  mobile?: string;
  dialCode?: string;
}): Promise<SignUpResponse> => {
  const addr = payload.address || {};
  const { addressLine, ...rest } = addr;
  const mappedAddress = {
    ...rest,
    addressLine1: addr.addressLine1 || addressLine || "",
    addressLine2: addr.addressLine2 || "",
  };
  return request("post", `${server}/accessmanager/organisation/v1/signup`, {
    data: {
      ...payload,
      address: mappedAddress,
      type: payload.type || "B2B",
      planId: payload.planId || "",
      planCode: payload.planCode || "",
    },
    skipAuth: true,
    unwrap: false,
  });
};

export const createZohoCustomer = (
  customer: ZohoCustomerPayload,
): Promise<{ [key: string]: unknown }> =>
  request("post", `${server}/subscription/v1/zoho-customer`, {
    data: customer,
    unwrap: false,
  });

// ---- OTP (Register / Verify) ----

// The service stores the account type upper cased (CORPORATE / TRAINER) and
// matches it case-sensitively, so send that form rather than title case.
function formatVerifyAccountType(
  accountType?: string | null,
): "CORPORATE" | "TRAINER" {
  return accountType?.toUpperCase() === "TRAINER" ? "TRAINER" : "CORPORATE";
}

export const verifyRegisterOTP = async (payload: {
  email: string;
  otp: string;
  accountType?: string;
}): Promise<{ message: string; [key: string]: unknown }> => {
  const response = await request<{ message: string; authChallenge?: string }>(
    "post",
    `${server}/accessmanager/user/v1/verify`,
    {
      data: {
        email: payload.email,
        otp: payload.otp,
        accountType: formatVerifyAccountType(
          payload.accountType || localStorage.getItem("accountType"),
        ),
      },
      skipAuth: true,
      unwrap: false,
    },
  );
  if (response.authChallenge)
    loginChallenge = {
      email: payload.email.trim().toLowerCase(),
      accountType: payload.accountType || "CORPORATE",
      proof: response.authChallenge,
      expires: Date.now() + 600_000,
    };
  return response;
};

export const resendOTP = (email: string): Promise<{ message: string }> =>
  request("post", `${server}/accessmanager/user/v1/resend-otp`, {
    data: {
      email,
      accountType: formatVerifyAccountType(
        localStorage.getItem("accountType") || "CORPORATE",
      ),
    },
    skipAuth: true,
    unwrap: false,
  });

// ---- Forgot / Reset Password ----

export const forgotPasswordGenerateOTP = (
  email: string,
): Promise<{ message: string; verifiedOtp?: boolean }> =>
  request("post", `${server}/accessmanager/user/v1/forgot-password`, {
    data: {
      username: email,
      accountType: formatVerifyAccountType(
        localStorage.getItem("accountType") || "CORPORATE",
      ),
    },
    skipAuth: true,
    unwrap: false,
  });

export const forgotPasswordVerifyOTP = (
  email: string,
  otp: string,
): Promise<{ message: string; verifiedOtp?: boolean }> =>
  request("post", `${server}/accessmanager/user/v1/forgot-password`, {
    data: {
      username: email,
      otp,
      otpGenerated: true,
      accountType: formatVerifyAccountType(
        localStorage.getItem("accountType") || "CORPORATE",
      ),
    },
    skipAuth: true,
    unwrap: false,
  });

export const resetPasswordWithOTP = (payload: {
  username: string;
  otp: string;
  verifyPassword: string;
  newPassword: string;
}): Promise<{ message: string; changedPassword?: boolean }> =>
  request("post", `${server}/accessmanager/user/v1/forgot-password`, {
    data: {
      ...payload,
      otpGenerated: true,
      otpVerified: true,
      accountType: formatVerifyAccountType(
        localStorage.getItem("accountType") || "CORPORATE",
      ),
    },
    skipAuth: true,
    unwrap: false,
  });

export const resetPassword = (payload: {
  currentPassword: string;
  newPassword: string;
  verifyPassword: string;
}): Promise<{ message: string }> =>
  request("post", `${server}/user/v1/reset-password`, {
    data: {
      username: storedUser().email as string | undefined,
      ...payload,
    },
    unwrap: false,
  });

// ---- Email URL Verification (Employee Activation) ----

export const getURLVerificationUser = (
  key: string,
): Promise<{ email: string; status: string; [key: string]: unknown }> =>
  request("get", `${server}/accessmanager/user/v1/url-verification`, {
    params: { key },
    skipAuth: true,
    unwrap: false,
  });

export const verifyEmployeeSetPassword = (payload: {
  email: string;
  key: string;
  password: string;
}): Promise<{ message: string }> =>
  request("put", `${server}/accessmanager/user/v1/url-verification`, {
    data: payload,
    skipAuth: true,
    unwrap: false,
  });

// ---- External Verifier ----

export const requestExternalVerifierOTP = (
  auditRequestId: string,
): Promise<{ message: string }> =>
  request("post", `${server}/accessmanager/verify/v1/audit-otp`, {
    data: { auditRequest: auditRequestId },
    skipAuth: true,
    unwrap: false,
  });

export const verifyExternalVerifierOTP = (payload: {
  auditRequest: string;
  otp: string;
  email?: string;
}): Promise<LoginResponse> =>
  request("post", `${server}/accessmanager/verify/v1/verify-otp`, {
    data: payload,
    skipAuth: true,
    unwrap: false,
  });

export const resendExternalVerifierOTP = (
  auditRequestId: string,
  email?: string,
): Promise<{ message: string }> =>
  request("post", `${server}/accessmanager/verify/v1/resend-otp`, {
    data: { auditRequest: auditRequestId, email },
    skipAuth: true,
    unwrap: false,
  });

export interface RegistrationIndustryOption {
  id: number;
  label: string;
}

export const getRegistrationIndustries = async (): Promise<
  RegistrationIndustryOption[]
> => {
  const groups = await request<
    { name: string; children: { id: number | string; name: string }[] }[]
  >("get", `${server}/accessmanager/organisation/v1/industries`, {
    skipAuth: true,
    unwrap: false,
  });
  if (!Array.isArray(groups)) throw new Error("Unable to load industries");
  return groups.flatMap((group) =>
    Array.isArray(group.children)
      ? group.children
          .map((child) => ({
            id: Number(child.id),
            label: `${group.name}: ${child.name}`,
          }))
          .filter((option) => Number.isSafeInteger(option.id) && option.id > 0)
      : [],
  );
};

// ---- Supplier ----

export interface SupplierInvitation {
  name?: string | null;
  email?: string | null;
  registration_id?: string | null;
  customer_name?: string | null;
  contact_name?: string | null;
  phone?: string | null;
  dial_code?: string | null;
  industry?: number | string | null;
  address?: { [Key in keyof SignUpAddress]?: string | null } | null;
  message?: string;
  [key: string]: unknown;
}

export const validateSupplier = (
  supplierId: string,
  otp: string,
): Promise<SupplierInvitation> =>
  request("post", `${server}/accessmanager/srm/v1/validate-supplier`, {
    data: { supplierId, otp },
    skipAuth: true,
    unwrap: false,
  });

export const activateSupplier = (
  payload: {
    supplier: string;
    otp: string;
    contact?: string;
    phone?: string;
    address?: string;
  },
  token?: string,
): Promise<{ message: string }> =>
  request("put", `${server}/srm/v1/activate-supplier`, {
    data: payload,
    ...authFor(token),
    unwrap: false,
  });

// ---- User Profile ----

export const viewProfile = (
  token?: string,
): Promise<{ [key: string]: unknown }> =>
  request("get", `${server}/user/v1/userprofile`, {
    ...authFor(token),
    unwrap: false,
  });

export const updateProfile = (
  payload: Record<string, unknown>,
  token?: string,
): Promise<{ [key: string]: unknown }> =>
  request("put", `${server}/user/v1/userprofile`, {
    data: payload,
    ...authFor(token),
    unwrap: false,
  });

export const getUserOnboardingStatus = (
  username?: string,
): Promise<{ enabled: boolean; [key: string]: unknown }> =>
  request("get", `${server}/accessmanager/user/v1/user-onboarding-status`, {
    params: {
      username: username || localStorage.getItem("userName"),
      authChallenge: challengeFor(
        username || localStorage.getItem("userName") || "",
      ),
      accountType: localStorage.getItem("accountType") || "CORPORATE",
    },
    skipAuth: true,
    unwrap: false,
  });

// ---- Session ----

export const getActiveSessions = (
  username?: string | null,
  token?: string,
): Promise<unknown> =>
  request("get", `${server}/security/v1/sessions`, {
    params: username ? { username } : {},
    ...authFor(token),
    unwrap: false,
  });

export const updateSession = (
  sessionId: string,
  token?: string,
): Promise<{ message: string }> =>
  request("put", `${server}/security/v1/session`, {
    data: { sessionId },
    ...authFor(token),
    withCredentials: true,
    unwrap: false,
  });

// ---- Logout ----

export const logout = async (): Promise<void> => {
  await withAuthCookieLock(async () => {
    const response = await request<{ loggedOut?: boolean }>(
      "post",
      `${server}/security/v1/logout`,
      {
        data: {},
        headers: await csrfHeaders(),
        withCredentials: true,
        skipAuth: true,
        unwrap: false,
        timeout: 15_000,
      },
    );
    if (response.loggedOut !== true)
      throw new Error("Unable to end the server session.");
  });
};
